import tailwind from "../../../../../tailwind.config"
import { Stack, Box } from "@chakra-ui/react"
import { Checkbox } from "../../../ui/checkbox"
import { useState } from "react"
import useStore from "../../../../store"
import UtilizationFilter from "./UtilizationFilter"
import LocationFilter from "./LocationFilter"

export default function FilterOptions({ setFilterParams, filterParams, handleFilterByRadius }) {
  const setSelectedDealerScopeLabel = useStore((state) => state.setSelectedDealerScopeLabel)
  const [showSlider, setShowSlider] = useState(false)
  const [overCreditLimit, setOverCreditLimit] = useState(false)
  const grey300 = tailwind.theme.colors["grey-300"]
  const setDealersPage = useStore((state) => state.setDealersPage)

  const isAuditSorted = filterParams['sort[field_audit_due_on]'] === true && filterParams['sort[field_audit_due_on_direction]'] === "ASC" && filterParams['filter[has_current_units]'] === "true"

  const handleFilterByScope = (scope, label) => {
    if (filterParams['scope'] !== scope) {
      clearFilters()
      setFilterParams({
        ...filterParams,
        "scope": scope
      })
      setSelectedDealerScopeLabel(label)
    } else {
      clearFilters()
    }
  }

  const handleUtilizationCheckboxChange = () => {
    if (showSlider || (filterParams.hasOwnProperty('utilization[min]') && filterParams.hasOwnProperty('utilization[max]'))) {
      clearFilters()
    } else {
      clearFilters()
      setShowSlider(true)
    }
  }

  const handleSortByAuditDueOnCheckboxChange = () => {
    if (isAuditSorted) {
      clearFilters()
    } else {
      clearFilters()
      setFilterParams({
        ...filterParams,
        "sort[field_audit_due_on]": true,
        "sort[field_audit_due_on_direction]": "ASC",
        "filter[has_current_units]": "true"
      })
      setSelectedDealerScopeLabel("Audit Due Date")
    }
  }

  const clearFilters = () => {
    const {
      'utilization[min]': __,
      'utilization[max]': _____,
      'sort[field_audit_due_on]': ___,
      'sort[field_audit_due_on_direction]': ____,
      'filter[has_current_units]': ______,
      scope,
      ...remainingFilters
    } = filterParams
    setFilterParams(remainingFilters)
    setSelectedDealerScopeLabel('')
    setShowSlider(false)
    setDealersPage(1)
  }

  const handleOverCreditLimitChange = () => {
    if (overCreditLimit) {
      setOverCreditLimit(false)
      clearFilters()
    } else {
      setFilterParams({
        ...filterParams,
        "utilization[min]": "100",
        "utilization[max]": "1000"
      })
      setSelectedDealerScopeLabel("Over Credit Limit")
      setOverCreditLimit(true)
    }
  }

  return(
    <Box divideY={1} divideColor={grey300}>
      <LocationFilter
        dealerTableFilterParams={filterParams}
        handleFilterByRadius={handleFilterByRadius}
      />

      <Stack mb={6}>
        <h4 className="font-semibold text-lg my-2">Dealers with...</h4>
        <Checkbox
          data-testid='untouched-units'
          checked={filterParams['scope'] === "with_untouched_units"}
          onCheckedChange={() => handleFilterByScope("with_untouched_units", "with Units Untouched 60+ Days")}
        >
          Units Untouched 60+ Days
        </Checkbox>
        <Checkbox
          data-testid='sold-units'
          checked={filterParams['scope'] === "with_sold_units"}
          onCheckedChange={() => handleFilterByScope("with_sold_units", "with Sold Units")}
        >
          Sold Units
        </Checkbox>
        <Checkbox
          data-testid='review-due'
          checked={filterParams['scope'] === "with_review_coming_due"}
          onCheckedChange={() => handleFilterByScope("with_review_coming_due", "with Review Coming Due")}
        >
          Review Coming Due
        </Checkbox>
        <Checkbox
          data-testid='repo-units'
          checked={filterParams['scope'] === "with_repo_units"}
          onCheckedChange={() => handleFilterByScope("with_repo_units", "with Repo Units")}
        >
          Repo Units
        </Checkbox>
        <Checkbox
          data-testid='past-due-units'
          checked={filterParams['scope'] === "with_past_due_units"}
          onCheckedChange={() => handleFilterByScope("with_past_due_units", "with Past Due Units")}
        >
          Past Due Units
        </Checkbox>
        <Checkbox
          data-testid='with-nsfs'
          checked={filterParams['scope'] === "with_nsfs"}
          onCheckedChange={() => handleFilterByScope("with_nsfs", "with Active NSFs")}
        >
          Active NSFs
        </Checkbox>
        <Checkbox
          data-testid='with-unreconciled-field-audits'
          checked={filterParams['scope'] === "with_unreconciled_field_audits"}
          onCheckedChange={() => handleFilterByScope("with_unreconciled_field_audits", "with Unreconciled Field Audits")}
        >
          Unreconciled Field Audits
        </Checkbox>
        <Checkbox
          data-testid='utilization'
          checked={showSlider}
          onCheckedChange={handleUtilizationCheckboxChange}
        >
          Utilization
        </Checkbox>
        {showSlider && (
          <UtilizationFilter />
        )}
        <Checkbox
          data-testid='transportation-enabled'
          checked={filterParams['scope'] === "transportation_enabled"}
          onCheckedChange={() => handleFilterByScope("transportation_enabled", "with Transportation Enabled")}
        >
          Transportation Enabled
        </Checkbox>
        <Checkbox
          data-testid='has-carpool-orders'
          checked={filterParams['scope'] === "has_carpool_orders"}
          onCheckedChange={() => handleFilterByScope("has_carpool_orders", "with Transport Orders")}
        >
          Transport Orders
        </Checkbox>
      </Stack>


      <Stack mb={6}>
        <h4 className="font-semibold text-lg my-2">Proactive Filters</h4>
        <Checkbox
          data-testid='untouched-in-past-week'
          checked={filterParams['scope'] === "untouched_past_week"}
          onCheckedChange={() => handleFilterByScope("untouched_past_week", "Untouched In Past Week")}
        >
          Untouched In Past Week
        </Checkbox>
        <Checkbox
          data-testid='no-floors-this-week'
          checked={filterParams['scope'] === "no_floors_this_week"}
          onCheckedChange={() => handleFilterByScope("no_floors_this_week", "No Floors This Week")}
        >
          No Floors This Week
        </Checkbox>
        <Checkbox
          data-testid='over-credit-limit'
          checked={overCreditLimit}
          onCheckedChange={handleOverCreditLimitChange}
        >
          Over Credit Limit
        </Checkbox>
      </Stack>


      <Stack>
        <h4 className="font-semibold text-lg my-2">Sort By</h4>
        <Checkbox
          data-testid='sort-by-audit-due-on'
          checked={isAuditSorted}
          onCheckedChange={handleSortByAuditDueOnCheckboxChange}
        >
          Audit Due Date
        </Checkbox>
      </Stack>
    </Box>
  )
}